import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LEAGUE_FEATURES } from '../../core/league/league';
import { leagueTeamAbbrevs, teamAbbrevLookup } from '../../core/model/teams';
import { addDays, getRoundDate } from '../../core/time/roundDate';
import { leagueSettings, settings, type ScoreFilter } from '../../core/settings/settings';
import { sessionGet, sessionSet } from '../../core/settings/storage';
import { useSetting } from '../../core/settings/useSetting';
import { getEngine } from '../../core/sync/appEngine';
import { useAppState } from '../../core/sync/useAppState';
import { useEphemeralSet } from '../common/ephemeral';
import { EyeIcon } from '../common/EyeIcon';
import { Segmented } from '../common/Segmented';
import { nextSortOrder } from '../common/sort';
import { useNow } from '../common/useNow';
import type { AppData } from '../appData';
import { DayRow } from './DayRow';
import { GameCard } from './GameCard';
import { buildGameCard, isGameLocked } from './gameViewModel';
import { PointTracker } from './PointTracker';
import { buildPointTracker, gaugeTop } from './pointTrackerViewModel';
import { RoundPlayers } from './RoundPlayers';
import { TeamPicker } from './TeamPicker';
import { buildPointsSeries, buildRoundPlayers, buildRoundTotal, roundGames } from './todayViewModel';
import './today.css';

const LONG_PRESS_MS = 500;

// The round on which the previous game day was opened (tapping the date). It
// is shown only while that round is still the current one, so a new game day
// always starts with the current round; kept for the browser tab's session.
const PREVIOUS_DAY_KEY = 'nhlcountries_previousDayFor';

// Tänään: the current round's scoresheet and our players' stat lines, behind
// the score display mode (all / our players / none) and the hidden teams.
// Tapping the date shows the previous game day instead (NHL only).
export default function TodayTab({ data }: { data: AppData }) {
    const { t } = useTranslation();
    const now = useNow();
    const roundDate = useMemo(() => getRoundDate(now, data.scores), [now, data.scores]);
    const features = LEAGUE_FEATURES[data.league];
    const teamAbbrev = useMemo(() => teamAbbrevLookup(data.league, data.standings), [data.league, data.standings]);

    const [view, setView] = useSetting(settings.todayView);
    const [trackerOpen, setTrackerOpen] = useSetting(settings.todayPointTracker);
    const [scoreFilter, setScoreFilter] = useSetting(settings.scoreFilter);
    const [hideEnabled, setHideEnabled] = useSetting(settings.hideTeams);
    const [hiddenList, setHiddenList] = useSetting(leagueSettings(data.league).hideTeamsList);
    const [hideDay, setHideDay] = useSetting(settings.hideTeamsDay);
    const [language] = useSetting(settings.language);
    const [skaterOrder, setSkaterOrder] = useSetting(settings.roundSkaterSortOrder);
    const [goalieOrder, setGoalieOrder] = useSetting(settings.roundGoalieSortOrder);
    const [collapsed, setCollapsed] = useEphemeralSet('collapsed-games');
    const [revealed, setRevealed] = useEphemeralSet('revealed-games');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [statsUnlockedFor, setStatsUnlockedFor] = useState<string>();
    const [previousFor, setPreviousFor] = useState(() => sessionGet(PREVIOUS_DAY_KEY));
    const { previousDay } = useAppState();

    const showPrevious = features.previousDay && previousFor === roundDate;
    const previousDate = addDays(roundDate, -1);
    const shownDate = showPrevious ? previousDate : roundDate;
    const togglePrevious = () => {
        const next = showPrevious ? '' : roundDate;
        sessionSet(PREVIOUS_DAY_KEY, next);
        setPreviousFor(next);
    };
    useEffect(() => {
        if (showPrevious) {
            getEngine().loadPreviousDay(previousDate);
        }
    }, [showPrevious, previousDate]);

    const previous = previousDay.value?.date === previousDate && previousDay.value.country === data.country
        ? previousDay.value
        : undefined;
    // While the previous game day loads (or failed to load): a message in place of the lists.
    const previousPending = showPrevious && previous === undefined;
    const scores = showPrevious ? previous?.scores ?? [] : data.scores;
    const players = showPrevious ? previous?.players ?? [] : data.players;
    const statsUnlocked = statsUnlockedFor === shownDate;

    // A new round always starts with the team hiding on, so that the morning
    // check does not spoil games that are watched later.
    useEffect(() => {
        if (hideDay !== roundDate) {
            setHideDay(roundDate);
            setHideEnabled(true);
        }
    }, [hideDay, roundDate, setHideDay, setHideEnabled]);

    // Long press on the hide button toggles the hiding without opening the picker.
    const pressTimer = useRef<ReturnType<typeof setTimeout>>();
    const longPressed = useRef(false);
    const cancelLongPress = () => {
        clearTimeout(pressTimer.current);
        pressTimer.current = undefined;
    };
    useEffect(() => cancelLongPress, []);

    const hiddenTeams = hideEnabled ? hiddenList : [];
    const games = roundGames(scores, shownDate);
    const cards = games.map(game => buildGameCard(game, {
        players,
        scoreFilter,
        hiddenTeams,
        revealed: revealed.has(String(game.gameId)),
        teamAbbrev,
        t,
        language,
        now,
    }));

    const total = buildRoundTotal(games, players, scoreFilter, hiddenTeams, revealed, teamAbbrev);
    const round =buildRoundPlayers(players, games, shownDate, hiddenTeams, skaterOrder, goalieOrder, revealed, teamAbbrev);
    const statsLocked = scoreFilter === 'none' && !statsUnlocked &&
        (games.length === 0 || games.some(game => isGameLocked(game, scoreFilter, [], revealed, teamAbbrev)));

    const hiddenCount = hiddenTeams.length;
    const pendingMessage = previousDay.status === 'error'
        ? (
            <div className="state-message" role="alert">
                <p>{t('state_noData')}</p>
                <button type="button" onClick={() => getEngine().loadPreviousDay(previousDate)}>{t('state_retry')}</button>
            </div>
        )
        : <p className="state-message" role="status">{t('state_loading')}</p>;
    const lockedMessage = (
        <div className="state-message" data-testid="players-locked">
            <p>{t('today_locked')}</p>
            <button type="button" onClick={() => setStatsUnlockedFor(shownDate)}>{t('today_showAnyway')}</button>
        </div>
    );
    // "Näytä silti" opens the tracker's gauge along with its list, so that the two agree.
    const trackerTotal = statsUnlocked ? buildRoundTotal(games, players, 'all', hiddenTeams, revealed, teamAbbrev) : total;
    const filterOptions: Array<{ value: ScoreFilter; label: string; ariaLabel?: string }> = [
        { value: 'all', label: t('filter_all') },
        { value: 'filtered', label: data.country },
        { value: 'none', label: t('filter_none') },
    ];

    return (
        <div className="today" data-testid="tab-today" data-view={view}>
            <DayRow
                roundDate={shownDate}
                previous={showPrevious}
                onToggleDay={features.previousDay ? togglePrevious : undefined}
                total={total}
                trackerOpen={trackerOpen}
                onToggleTracker={() => setTrackerOpen(!trackerOpen)}
            />
            {trackerOpen
                ? (
                    <PointTracker
                        points={trackerTotal.points}
                        top={gaugeTop(data.country, buildPointsSeries(data.points, shownDate, undefined).values, trackerTotal.points)}
                        tracker={buildPointTracker({ players, games, date: shownDate, hiddenTeams, revealed, teamAbbrev, t, now })}
                        message={previousPending
                            ? pendingMessage
                            : games.length === 0
                            ? <p className="state-message">{t('state_noGames')}</p>
                            : statsLocked ? lockedMessage : undefined}
                    />
                )
                : (
            <>
            <div className="toolbar">
                <Segmented
                    className="today-view-switch"
                    label={t('today_view')}
                    value={view}
                    onChange={setView}
                    options={[
                        { value: 'games', label: t('today_games') },
                        { value: 'players', label: t('today_players') },
                    ]}
                />
                <Segmented
                    className="end"
                    size="sm"
                    label={t('filter_label')}
                    value={scoreFilter}
                    onChange={setScoreFilter}
                    options={filterOptions}
                />
                <button
                    type="button"
                    className={`hide-button${hiddenCount > 0 ? ' on' : ''}`}
                    aria-label={t('hide_button')}
                    onClick={() => {
                        if (longPressed.current) {
                            longPressed.current = false;
                            return;
                        }
                        setPickerOpen(true);
                    }}
                    onPointerDown={event => {
                        if (event.pointerType === 'mouse' && event.button !== 0) return;
                        longPressed.current = false;
                        cancelLongPress();
                        pressTimer.current = setTimeout(() => {
                            pressTimer.current = undefined;
                            longPressed.current = true;
                            setHideEnabled(!hideEnabled);
                        }, LONG_PRESS_MS);
                    }}
                    onPointerUp={cancelLongPress}
                    onPointerLeave={cancelLongPress}
                    onPointerCancel={cancelLongPress}
                    onContextMenu={event => event.preventDefault()}
                >
                    <EyeIcon />
                    {hiddenCount > 0 && <span className="hide-count" data-testid="hidden-count">{hiddenCount}</span>}
                </button>
            </div>

            <div className="today-columns">
                <section className="today-games" aria-label={t('today_games')}>
                    <h2 className="col-head">{t('today_games')}</h2>
                    {previousPending
                        ? pendingMessage
                        : cards.length === 0
                        ? <p className="state-message">{t('state_noGames')}</p>
                        : cards.map(card => (
                            <GameCard
                                key={card.key}
                                card={card}
                                collapsed={collapsed.has(String(card.key))}
                                onToggleCollapsed={() => setCollapsed(String(card.key), !collapsed.has(String(card.key)))}
                                onReveal={() => setRevealed(String(card.key), true)}
                            />
                        ))}
                </section>
                <section className="today-players" aria-label={t('today_players')}>
                    <h2 className="col-head">{t('today_players')}</h2>
                    {previousPending
                        ? pendingMessage
                        : statsLocked
                        ? lockedMessage
                        : (
                            <>
                                {round.withheld &&
                                    <p className="players-note" data-testid="players-hidden-note" role="note">{t('today_hiddenTeamsPlayers')}</p>}
                                <RoundPlayers
                                    skaters={round.skaters}
                                    goalies={round.goalies}
                                    skaterOrder={skaterOrder}
                                    goalieOrder={goalieOrder}
                                    onSkaterSort={column => setSkaterOrder(nextSortOrder(skaterOrder, column))}
                                    onGoalieSort={column => setGoalieOrder(nextSortOrder(goalieOrder, column))}
                                    skaterToi={features.skaterToi}
                                />
                            </>
                        )}
                </section>
            </div>
            </>
                )}

            {pickerOpen &&
                <TeamPicker
                    teams={leagueTeamAbbrevs(data.league, data.standings)}
                    enabled={hideEnabled}
                    selected={hiddenList}
                    onEnabledChange={setHideEnabled}
                    onSelectedChange={setHiddenList}
                    onClose={() => setPickerOpen(false)}
                />}
        </div>
    );
}
